import { useContext, useEffect, useState } from 'react';
import {
  gql, useLazyQuery, useMutation, useQuery,
} from '@apollo/client';
import { min } from 'lodash';
import { AssignPortfolioToSubAccountVisual } from './assignPortfolioToSubAccount.visual';
import { WorkflowContext } from '../../workflowCompletion';
import { UserContext } from '../../../../../providers/userContextProvider';

const AVAILABLE_THEMES = `#graphql
  availableThemes {
    id
    key
    translatedName { en fr}
    translatedDescription { en fr}
    core
  }
`;

const SUGGESTED_PRODUCT = `#graphql
  id
  translatedName { en fr}
  url
  children {
    id
    percentage
    financialProduct {
      id
      translatedName { en fr}
      primaryAssetClass {
        id
        translatedName { en fr}
        translatedDescription { en fr}
        key
      }
      secondaryAssetClass {
        id
        translatedName { en fr}
        translatedDescription { en fr}
        key
      }
    }
  }
  theme {
    id
  }
`;

const UPDATE_SUB_ACCOUNT = gql`
  mutation updateSubAccount($input: UpdateSubAccountInput!) {
    updateSubAccount(input: $input) {
      subAccount {
        id
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
      }
    }
  }
`;

const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
        suitabilityScore
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
      }
    }
  }
`;

const FETCH_SUB_ACCOUNT = gql`
  query fetchSubAccount($subAccountId: ObjectID!) {
    fetchSubAccount(subAccountId: $subAccountId) {
      subAccount {
        id
        suitabilityScore
        ${'' /* custodianSuitabilityScore */}
        ${AVAILABLE_THEMES}
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
        financialProduct {
          id
          theme {
            id
          }
        }
      }
    }
  }
`;

const FETCH_GOAL = gql`
  query fetchGoal($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        suitabilityScore
        ${AVAILABLE_THEMES}
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
        financialProduct {
          id
          theme {
            id
          }
        }
      }
    }
  }
`;

const FETCH_USER_SUITABILITY = gql`
  query fetchUserSuitability($userId: ObjectID!) {
    fetchUser(userId: $userId) {
      user {
        id
        suitabilityScore
      }
    }
  }
`;

const FETCH_HOUSEHOLD_SUITABILITY = gql`
  query fetchHouseholdSuitability($clientGroupId: ObjectID!) {
    fetchClientGroup(clientGroupId: $clientGroupId) {
      clientGroup {
        primaryUser {
          id
          suitabilityScore
        }
      }
    }
  }
`;

const SUGGEST_PORTFOLIO = gql`
  query suggestPortfolio($suitabilityScore: Int!, $organizationId: ObjectID!, $themeId: ObjectID) {
    suggestFinancialProduct(input: {
      suitabilityScore: $suitabilityScore
      organizationId: $organizationId
      themeId: $themeId
    }) {
      financialProduct {
        ${SUGGESTED_PRODUCT}
      }
    }
  }
`;

export const FETCH_MODEL_PORTFOLIOS = gql`
  query fetchModelPortfolios($input: FetchModelPortfoliosInput!) {
    fetchModelPortfolios(input: $input) {
      modelPortfolios {
      riskReturnLevel
      ${SUGGESTED_PRODUCT}
      }
    }
  }
`;

const FETCH_THEMES = gql`
  query fetchThemes($input: FetchThemesInput!) {
    fetchThemes(input: $input) {
      themes {
        id
        key
        translatedName {
          en
          fr
        }
        translatedDescription {
          en
          fr
        }
        core
      }
    }
  }
`;

const MOCK_CUSTODIAN_SUITABILITY_SCORE = 10;

export const AssignPortfolioToSubAccount = ({
  options,
  stepLoading,
  workflowCompletion,
  objectId,
  objectType = 'USER',
  onNext,
}: {
  options: any;
  objectId: string;
  objectType?: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const { workflowData } = useContext(WorkflowContext);
  const [objectData, setObjectData] = useState<any>({
    themeId: null,
    financialProduct: null,
    availableThemes: [],
  });
  const [suggestedFinancialProduct, setSuggestedFinancialProduct] = useState<any>(null);
  const [lockThemeSelection, setLockThemeSelection] = useState<boolean>(false);
  const [noSuggestion, setNoSuggestion] = useState<boolean>(false);

  const setPortfolioOnGoal = options.setPortfolioOnGoal && activeOrganization?.allowPortfolioPerGoal;
  const isHardcodedWorkflowWithGoal = workflowData.currentGoalId && workflowCompletion.id === '123';
  const manuallyAssignModelPortfolios = options?.manuallyAssignModelPortfolios;

  const useExternalSuitabilityScore = options.useExternalSuitabilityScore && process.env.REACT_APP_ENV !== 'production';

  const { data, loading: fetchSubAccountLoading } = useQuery(FETCH_SUB_ACCOUNT, {
    variables: { subAccountId: workflowData.currentSubAccountId },
    skip: setPortfolioOnGoal || isHardcodedWorkflowWithGoal,
    fetchPolicy: 'no-cache',
  });

  const { data: goalData, loading: fetchGoalLoading } = useQuery(FETCH_GOAL, {
    variables: { goalId: workflowData.currentGoalId },
    skip: !setPortfolioOnGoal && !isHardcodedWorkflowWithGoal,
    fetchPolicy: 'no-cache',
  });

  const { loading: allThemesLoading, data: allThemesData } = useQuery(FETCH_THEMES, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          state: 'ACTIVE',
        },
      },
    },
    skip: !manuallyAssignModelPortfolios,
    onCompleted: (d: any) => {
      setObjectData((prevState: any) => ({
        ...prevState,
        availableThemes: d.fetchThemes.themes,
      }));
    },
  });

  const { loading: userLoading } = useQuery(objectType === 'HOUSEHOLD' ? FETCH_HOUSEHOLD_SUITABILITY : FETCH_USER_SUITABILITY, {
    variables: { ...(objectType === 'HOUSEHOLD' ? { clientGroupId: objectId } : { userId: objectId }) },
    onCompleted: (d: any) => {
      setObjectData((prevState: any) => ({
        ...prevState,
        userSuitability: objectType === 'HOUSEHOLD'
          ? d.fetchClientGroup?.clientGroup?.primaryUser?.suitabilityScore
          : d.fetchUser?.user?.suitabilityScore,
      }));
    },
  });

  const [suggestPortfolio, { loading: suggestPortfolioLoading }] = useLazyQuery(SUGGEST_PORTFOLIO);
  const [fetchModelPortfolios, { loading: modelPortfolioLoading }] = useLazyQuery(FETCH_MODEL_PORTFOLIOS);

  const fetchAndSetModelPortfolios = async () => {
    await fetchModelPortfolios({
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization.id,
            themeId: objectData.themeId,
            state: 'ACTIVE',
          },
          pagination: { perPage: 100 },
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: (d: any) => {
        setObjectData((prevState: any) => ({
          ...prevState,
          availableFinancialProducts: d.fetchModelPortfolios.modelPortfolios,
        }));
      },
    });
  };

  const portfolioOnGoalOrExternalSuitability = setPortfolioOnGoal || useExternalSuitabilityScore;
  const suggestPortfolioForGoalOrExternalSuitability = async () => {
    if (manuallyAssignModelPortfolios) await fetchAndSetModelPortfolios();

    if (objectData.userSuitability) {
      await suggestPortfolio({
        variables: {
          suitabilityScore: useExternalSuitabilityScore ? objectData.custodianSuitabilityScore : min([objectData.suitabilityScore, objectData.userSuitability]),
          organizationId: activeOrganization.id,
          themeId: objectData.themeId,
        },
        fetchPolicy: 'network-only',
        onCompleted: (d: any) => {
          const suggestionMatchesTheme: boolean = d.suggestFinancialProduct.financialProduct.theme.id === objectData.themeId;
          if (!suggestionMatchesTheme) setNoSuggestion(true);

          setSuggestedFinancialProduct(d.suggestFinancialProduct.financialProduct);
          setObjectData((prevState: any) => ({
            ...prevState,
            financialProduct: suggestionMatchesTheme ? d?.suggestFinancialProduct?.financialProduct : undefined,
          }));
        },
      });
    }
  };

  const hasGoalAndSubaccountId = workflowData.currentSubAccountId && workflowData.currentGoalId;
  const suggestPortfolioGeneralUseCase = async () => {
    if (manuallyAssignModelPortfolios) await fetchAndSetModelPortfolios();

    if (objectData.userSuitability) {
      suggestPortfolio({
        variables: {
          suitabilityScore: min([objectData.suitabilityScore, objectData.userSuitability]),
          organizationId: activeOrganization.id,
          themeId: objectData.themeId,
        },
        fetchPolicy: 'network-only',
        onCompleted: async (d: any) => {
          const suggestionMatchesTheme: boolean = d.suggestFinancialProduct.financialProduct.theme.id === objectData.themeId;
          if (!suggestionMatchesTheme) setNoSuggestion(true);

          setSuggestedFinancialProduct(d.suggestFinancialProduct.financialProduct);
          if (isHardcodedWorkflowWithGoal && goalData?.fetchGoal?.goal?.financialProduct?.theme?.id) setLockThemeSelection(true);
          setObjectData((prevState: any) => ({
            ...prevState,
            financialProduct: suggestionMatchesTheme ? d?.suggestFinancialProduct?.financialProduct : undefined,
          }));
        },
      });
    }
  };

  const hasNoSubaccountId = workflowData.currentGoalId && !workflowData.currentSubAccountId;
  const suggestPortfolioGoalOnly = async () => {
    if (manuallyAssignModelPortfolios) await fetchAndSetModelPortfolios();

    if (objectData.userSuitability) {
      updateGoal({
        onCompleted: (d: any) => {
          setSuggestedFinancialProduct(d.updateGoal.goal.suggestedFinancialProduct);
        },
        variables: {
          input: {
            goalId: workflowData.currentGoalId,
            themeId: objectData.themeId || undefined,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (data && !manuallyAssignModelPortfolios) {
      const fetchedSubAccountThemeId = data?.fetchSubAccount?.subAccount?.financialProduct?.theme?.id;
      const defaultTheme = data?.fetchSubAccount?.subAccount?.availableThemes.find((theme: any) => theme.core === true);
      setObjectData((prevState: any) => ({
        ...prevState,
        availableThemes: data?.fetchSubAccount?.subAccount?.availableThemes,
        suitabilityScore: data?.fetchSubAccount?.subAccount?.suitabilityScore,
        custodianSuitabilityScore: MOCK_CUSTODIAN_SUITABILITY_SCORE,
        themeId: fetchedSubAccountThemeId || (prevState.themeId ? prevState.themeId : defaultTheme?.id),
      }));
    }

    if (data && manuallyAssignModelPortfolios) {
      const defaultTheme = allThemesData?.fetchThemes?.themes?.find((theme: any) => theme.core === true);
      const fetchedSubAccountThemeId = data?.fetchSubAccount?.subAccount?.financialProduct?.theme?.id;
      setObjectData((prevState: any) => ({
        ...prevState,
        custodianSuitabilityScore: MOCK_CUSTODIAN_SUITABILITY_SCORE,
        suitabilityScore: data?.fetchSubAccount?.subAccount?.suitabilityScore,
        themeId: fetchedSubAccountThemeId || (prevState.themeId ? prevState.themeId : defaultTheme?.id),
      }));
    }
    // eslint-disable-next-line
  }, [data, setObjectData]);

  useEffect(() => {
    if (goalData && !manuallyAssignModelPortfolios) {
      const goalExistingThemeId = goalData?.fetchGoal?.goal?.financialProduct?.theme?.id;
      const defaultTheme = goalData?.fetchGoal?.goal?.availableThemes.find((theme: any) => theme.core === true);
      setObjectData((prevState: any) => ({
        ...prevState,
        suitabilityScore: goalData?.fetchGoal.goal.suitabilityScore || 0,
        custodianSuitabilityScore: MOCK_CUSTODIAN_SUITABILITY_SCORE, // goalData?.fetchGoal.goal.custodianSuitabilityScore
        availableThemes: goalData?.fetchGoal.goal.availableThemes,
        themeId: prevState.themeId ? prevState.themeId : goalExistingThemeId || defaultTheme?.id,
      }));
    }

    if (goalData && manuallyAssignModelPortfolios) {
      const goalExistingThemeId = goalData?.fetchGoal?.goal?.financialProduct?.theme?.id;
      const defaultTheme = allThemesData?.fetchThemes?.themes?.find((theme: any) => theme.core === true);
      setObjectData((prevState: any) => ({
        ...prevState,
        suitabilityScore: goalData?.fetchGoal.goal.suitabilityScore || 0,
        custodianSuitabilityScore: MOCK_CUSTODIAN_SUITABILITY_SCORE, // goalData?.fetchGoal.goal.custodianSuitabilityScore
        themeId: prevState.themeId ? prevState.themeId : goalExistingThemeId || defaultTheme?.id,
      }));
    }
    // eslint-disable-next-line
  }, [goalData, setObjectData]);

  const [updateSubAccount, { loading }] = useMutation(UPDATE_SUB_ACCOUNT);
  const [updateGoal, { loading: goalLoading }] = useMutation(UPDATE_GOAL);

  useEffect(() => {
    if (!objectData.themeId) return;

    if (portfolioOnGoalOrExternalSuitability) {
      suggestPortfolioForGoalOrExternalSuitability();
    } else {
      if (hasGoalAndSubaccountId) {
        suggestPortfolioGeneralUseCase();
      }
      if (hasNoSubaccountId) {
        suggestPortfolioGoalOnly();
      }
    }
    // eslint-disable-next-line
  }, [objectData.themeId, updateSubAccount, suggestPortfolio, workflowData.currentSubAccountId, options]);

  useEffect(() => {
    if (!objectData.availableFinancialProducts?.length) return;

    if (!objectData.userSuitability || noSuggestion) {
      const lowestSuggestion = objectData.availableFinancialProducts.reduce(
        (lowest: any, modelPortfolio: any) => (modelPortfolio.riskReturnLevel < lowest.riskReturnLevel ? modelPortfolio : lowest),
        objectData.availableFinancialProducts[0],
      );

      setObjectData((prevState: any) => ({
        ...prevState,
        financialProduct: lowestSuggestion,
      }));
      setNoSuggestion(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectData.availableFinancialProducts, objectData.themeId, noSuggestion]);

  const setPortfolio = async () => {
    if (!objectData.themeId || !objectData.financialProduct) return;

    if (setPortfolioOnGoal) {
      if (workflowData.currentGoalId) {
        await updateGoal({
          variables: {
            input: {
              goalId: workflowData.currentGoalId,
              financialProductId: manuallyAssignModelPortfolios ? objectData.financialProduct.id : suggestedFinancialProduct.id,
            },
          },
        });
      }

      if (workflowData.currentSubAccountId) {
        await updateSubAccount({
          variables: {
            input: {
              subAccountId: workflowData.currentSubAccountId,
              skipIPS: true,
            },
          },
        });
      }

      await onNext();
      return;
    }

    const shouldSkipIPS = !!(isHardcodedWorkflowWithGoal && objectData.themeId && goalData?.fetchGoal?.goal?.financialProduct?.theme?.id);

    await updateSubAccount({
      variables: {
        input: {
          subAccountId: workflowData.currentSubAccountId,
          themeId: objectData.themeId,
          financialProductId: shouldSkipIPS ? undefined : manuallyAssignModelPortfolios ? objectData.financialProduct.id : suggestedFinancialProduct.id,
          skipIPS: shouldSkipIPS || false,
        },
      },
    });
    await onNext();
  };

  return (
    <AssignPortfolioToSubAccountVisual
      options={options}
      objectData={objectData}
      suggestedFinancialProduct={suggestedFinancialProduct}
      updateObject={setObjectData}
      continueFunc={setPortfolio}
      loading={loading || fetchSubAccountLoading || stepLoading || goalLoading || fetchGoalLoading || modelPortfolioLoading || suggestPortfolioLoading || allThemesLoading || userLoading}
      workflowCompletion={workflowCompletion}
      lockThemeSelection={lockThemeSelection}
      manuallyAssignModelPortfolios={manuallyAssignModelPortfolios}
    />
  );
};

export default AssignPortfolioToSubAccount;
